"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva(
  "inline-flex items-center justify-center max-md:justify-start max-md:overflow-x-auto max-md:scrollbar-hide max-md:w-full",
  {
    variants: {
      variant: {
        default: "h-10 rounded-md bg-muted p-1 text-muted-foreground",
        underline: "h-10 gap-4",
        // Wraps on a phone rather than scrolling. Six tabs in a 40px-tall strip
        // with `overflow-x-auto` gave a box that slid on BOTH axes — sideways
        // because the row is wider than the screen, and a hair vertically
        // because the row no longer fits the fixed height — so the strip moved
        // under the thumb that was reaching for a tab. Height follows the rows,
        // and the x overflow is reset explicitly: `overflow-visible` alone
        // leaves `overflow-x-auto` from the base class standing.
        segmented:
          "h-10 gap-1 rounded-md border border-neutral-300 bg-white p-1 max-md:h-auto max-md:flex-wrap max-md:overflow-x-visible",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "rounded-sm px-3 py-1.5 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        underline: "px-1 pb-2 -mb-px border-b-2 border-transparent text-neutral-900 data-[state=active]:border-brand-500 data-[state=active]:text-brand-600",
        // The selected one is the primary action; the rest stay plain white so
        // the strip reads as a switch and not as disabled text.
        segmented:
          "rounded-sm px-3 py-1.5 text-neutral-900 data-[state=inactive]:hover:bg-neutral-200 data-[state=active]:bg-brand-500 data-[state=active]:text-white data-[state=active]:shadow-sm",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
